import Add from '@mui/icons-material/Add';
import CircularProgress from '@mui/material/CircularProgress';
import { t } from 'i18next';
import { ChangeEvent, useRef, useState } from 'react';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { Timesheet } from '~hooks/useTimesheets/models';
import useTimesheets from '~hooks/useTimesheets/useTimesheets';
import { useStores } from '~store/RootStore';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface TimesheetUploadButtonProps {
  onUploaded: (uploadedTimesheets: Timesheet[]) => void;
}

export default function TimesheetUploadButton({
  onUploaded,
}: TimesheetUploadButtonProps) {
  const { userStore } = useStores();
  const { createTimesheet } = useTimesheets();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);

  const companyId = getEffectiveUserCompanyId(userStore);
  const { canCreateTicket } = userStore.getPermissions();
  const isUploading = uploadProgress !== null;

  const handleFilesSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    // Reset the input so selecting the same file again still fires onChange.
    event.target.value = '';

    if (!companyId || files.length === 0) {
      return;
    }

    setUploadProgress({ done: 0, total: files.length });

    const uploadedTimesheets: Timesheet[] = [];

    // Upload sequentially so each timesheet's AI extraction is kicked off in order and the
    // user sees per-file progress. There is no batch endpoint — each file is its own create.
    // Each file is handled independently so one failure doesn't abort remaining uploads.
    for (const [index, file] of files.entries()) {
      try {
        const timesheet = await createTimesheet(companyId, { file });
        if (timesheet) {
          uploadedTimesheets.push(timesheet);
        }
      } catch {
        // Error already toasted by createTimesheet; continue with remaining files.
      }
      setUploadProgress({ done: index + 1, total: files.length });
    }

    setUploadProgress(null);
    onUploaded(uploadedTimesheets);
  };

  // The create endpoint requires create_ticket, so don't show an action that would 403.
  if (!canCreateTicket) {
    return null;
  }

  return (
    <>
      <input
        ref={fileInputRef}
        accept="image/*,application/pdf"
        hidden
        multiple
        onChange={handleFilesSelected}
        type="file"
      />
      <SmallButton
        color="brandV2Yellow"
        disabled={isUploading}
        onClick={() => fileInputRef.current?.click()}
        startIcon={isUploading ? <CircularProgress color="inherit" size={16} /> : <Add />}
      >
        {uploadProgress
          ? t('approvals.timesheets.uploading_progress', {
              done: uploadProgress.done,
              total: uploadProgress.total,
            })
          : t('approvals.timesheets.add_timesheet')}
      </SmallButton>
    </>
  );
}
